/**
 * Pure grouping for Website → "Where sessions come from".
 *
 * Every source row is grouped under its channel, so a channel's total is the
 * same number Overview shows for it. Until 13 Oct the table grouped only the
 * top 20 source rows: Meta's four rows (11 + 4 + 2 + 1) were split by the cut,
 * so Website said "Paid social (Meta) 11" while Overview said 18 for the same
 * 90 days and the same classifier.
 */

export interface ChannelSourceRow {
  channel: string;
  source: string;
  medium: string;
  sessions: number;
  visitors: number;
}

export interface ChannelGroup<R extends ChannelSourceRow> {
  label: string;
  sessions: number;
  /** The group's biggest source rows, at most `perGroup`. */
  rows: R[];
  /** Smaller rows of this group, summed: counted in `sessions`, not listed. */
  rest: { count: number; sessions: number };
}

/** GA-style "no medium" spellings. */
const PLACEHOLDER_MEDIUMS = new Set(['', '(none)', '(not set)', '(direct)']);

/** Mediums the API used for an untagged ad click before 13 Oct (now folded into "cpc" server-side). */
const CLICK_ID_MEDIUMS = new Set(['gclid', 'msclkid', 'li_fat_id']);

/**
 * Pure: an untagged ad click ("bing / msclkid") is that platform's paid click,
 * so it joins the platform's "source / cpc" row; a site tagged without a medium
 * joins its referral row. Visitors are summed, an upper
 * bound when one visitor came both ways; the API now merges them exactly.
 */
export function foldClickIdRows<R extends ChannelSourceRow>(rows: readonly R[]): R[] {
  const out = new Map<string, R>();
  for (const r of rows) {
    const m = r.medium.trim().toLowerCase();
    // "chatgpt.com" (utm_source, no medium) and "chatgpt.com / referral" (referrer) are one site:
    // with a host as source, no medium and "referral" mean the same and show as one row.
    const hostSource = r.source.includes('.');
    const medium = CLICK_ID_MEDIUMS.has(m) ? 'cpc' : hostSource && (PLACEHOLDER_MEDIUMS.has(m) || m === 'referral') ? 'referral' : r.medium;
    const key = `${r.channel}|${r.source.toLowerCase()}|${medium.toLowerCase()}`;
    const prev = out.get(key);
    out.set(
      key,
      prev
        ? { ...prev, sessions: prev.sessions + r.sessions, visitors: prev.visitors + r.visitors }
        : { ...r, medium },
    );
  }
  return [...out.values()];
}

/** Pure: rows grouped under `labelOf`, biggest group first, each listing its top `perGroup` rows. */
export function groupChannelRows<R extends ChannelSourceRow>(
  rows: readonly R[],
  labelOf: (r: R) => string,
  perGroup: number,
): ChannelGroup<R>[] {
  const map = new Map<string, { label: string; sessions: number; all: R[] }>();
  for (const r of rows) {
    const label = labelOf(r);
    const g = map.get(label) ?? { label, sessions: 0, all: [] };
    g.sessions += r.sessions;
    g.all.push(r);
    map.set(label, g);
  }
  return [...map.values()]
    .map((g) => {
      const sorted = [...g.all].sort((x, y) => y.sessions - x.sessions);
      const shown = sorted.slice(0, perGroup);
      const hidden = sorted.slice(perGroup);
      return {
        label: g.label,
        sessions: g.sessions,
        rows: shown,
        rest: { count: hidden.length, sessions: hidden.reduce((n, r) => n + r.sessions, 0) },
      };
    })
    .sort((x, y) => y.sessions - x.sessions);
}
